import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { FunnelButton } from "../FunnelButton";
import { FunnelBackButton } from "../FunnelBackButton";
import type { DisqualifiedResultConfig } from "@portal/app/_server/services/formFunnel/payloadTypes";
import { useFormFunnelHeadingLevel } from "../FormFunnelHeadingContext";

export interface DisqualifiedStepProps {
  /** Configuration for the disqualified result messaging and actions */
  config: DisqualifiedResultConfig;
  /** Callback when user clicks "Change My Answer" button */
  onChangeAnswer?: () => void;
  /** Callback when user clicks the redirect button */
  onRedirect?: () => void;
}

/**
 * Component displayed when a user is disqualified from a form funnel
 * based on their answer to a qualifying question.
 *
 * Provides configurable:
 * - Title and message
 * - "Change My Answer" button to return to a previous question
 * - Redirect button to navigate to an alternative (e.g., Penn Foster)
 */
export const DisqualifiedStep = ({ config, onChangeAnswer, onRedirect }: DisqualifiedStepProps) => {
  const headingLevel = useFormFunnelHeadingLevel();
  const {
    title = "We're Sorry",
    message = "Unfortunately, you do not qualify for this program.",
    showChangeAnswerButton = true,
    showRedirectButton = false,
    redirectButtonText = "View Alternative Program",
  } = config;

  return (
    <Box
      data-testid="disqualified-step"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
        p: 4,
        textAlign: "center",
        maxWidth: 600,
        mx: "auto",
        border: "1px solid #eee",
        borderRadius: 2,
        boxShadow: 1,
      }}
    >
      <Typography variant="h4" component={headingLevel}>
        {title}
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
        {message}
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="center"
        sx={{ mt: 2, width: "100%" }}
      >
        {showChangeAnswerButton && onChangeAnswer && (
          <FunnelBackButton onClick={onChangeAnswer} data-testid="change-answer-button">
            Change My Answer
          </FunnelBackButton>
        )}

        {showRedirectButton && onRedirect && (
          <FunnelButton onClick={onRedirect} data-testid="redirect-button">
            {redirectButtonText}
          </FunnelButton>
        )}
      </Stack>
    </Box>
  );
};
